import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  TrendingUp,
  AttachMoney,
  Receipt,
  Warning,
  CheckCircle,
  Edit,
  MoreVert,
  Visibility,
  PictureAsPdf
} from '@mui/icons-material'

import { useClient } from '../../hooks/useClients'
import { formatCurrency, formatDate, formatPhone, formatRUT, formatInvoiceStatus } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import ClientForm from '../../components/Clients/ClientForm'
import PdfViewer from '../../components/Common/PdfViewer'

const ClientDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const [showEditForm, setShowEditForm] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null)

  const { data: clientData, isLoading, error } = useClient(id)

  const handleBack = () => {
    navigate('/clients')
  }

  const handleEdit = () => {
    setShowEditForm(true)
  }

  const handleCloseEditForm = () => {
    setShowEditForm(false)
  }

  const handleInvoiceMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget)
    setSelectedInvoice(invoice)
  }

  const handleInvoiceMenuClose = () => {
    setAnchorEl(null)
    setSelectedInvoice(null)
  }

  const handleViewInvoice = () => {
    if (selectedInvoice) {
      navigate(`/invoices/${selectedInvoice.id}`)
    }
    handleInvoiceMenuClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAGADA': return 'success'
      case 'POR_COBRAR': return 'warning'
      case 'VENCIDA': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAGADA': return <CheckCircle />
      case 'POR_COBRAR': return <AttachMoney />
      case 'VENCIDA': return <Warning />
      default: return <Receipt />
    }
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a Clientes
        </Button>
        <Alert severity="error">
          Error cargando cliente: {error.message}
        </Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a Clientes
        </Button>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    )
  }

  const client = clientData
  const stats = client?.stats || {}

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Volver
        </Button>
        
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {client?.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {formatRUT(client?.rut)}
          </Typography>
        </Box>

        <RoleGuard requireAdmin>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Editar Cliente
          </Button>
        </RoleGuard>
      </Box>

      <Grid container spacing={3}>
        {/* Client Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mr: 2
                  }}
                >
                  {client?.name?.charAt(0)?.toUpperCase()}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {client?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatRUT(client?.rut)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Contact Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Información de Contacto
                </Typography>
                
                {client?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{client.email}</Typography>
                  </Box>
                )}
                
                {client?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{formatPhone(client.phone)}</Typography>
                  </Box>
                )}
                
                {client?.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary', mt: 0.2 }} />
                    <Typography variant="body2">{client.address}</Typography>
                  </Box>
                )}

                {!client?.email && !client?.phone && !client?.address && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Sin información de contacto
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Client Status */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estado del Cliente
                </Typography>
                <Chip
                  label={client?.active ? 'Activo' : 'Inactivo'}
                  color={client?.active ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Receipt sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {stats.totalInvoices || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Facturas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(stats.totalBilled || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Facturado
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AttachMoney sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(stats.pendingAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por Cobrar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Warning sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(stats.overdueAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vencido
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Invoices */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Facturas Recientes
              </Typography>
              
              {client?.invoices && client.invoices.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Vencimiento</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.invoices.map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {invoice.invoiceNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(invoice.issueDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(invoice.dueDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(invoice.totalAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={formatInvoiceStatus(invoice.visualStatus || invoice.status)}
                              color={getStatusColor(invoice.visualStatus || invoice.status)}
                              size="small"
                              variant="outlined"
                              icon={getStatusIcon(invoice.visualStatus || invoice.status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Más opciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleInvoiceMenuOpen(e, invoice)}
                              >
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Este cliente aún no tiene facturas
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Client Form */}
      <ClientForm
        open={showEditForm}
        onClose={handleCloseEditForm}
        client={client}
        title="Editar Cliente"
      />

      {/* Invoice Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleInvoiceMenuClose}
      >
        <MenuItem onClick={handleViewInvoice}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver Factura</ListItemText>
        </MenuItem>
        
        {selectedInvoice?.pdfUrl && (
          <MenuItem onClick={() => {
            setCurrentPdfUrl(selectedInvoice.pdfUrl)
            setPdfViewerOpen(true)
            setAnchorEl(null)
          }}>
            <ListItemIcon>
              <PictureAsPdf fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver PDF</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={currentPdfUrl}
        title={`Factura ${selectedInvoice?.invoiceNumber}`}
        fileName={`${selectedInvoice?.invoiceNumber || 'factura'}.pdf`}
      />
    </Box>
  )
}

export default ClientDetailPage
